/**
 * Planting trees, the one thing the two looks do differently with plants. Today's look builds a
 * tree from a trunk and a round crown. The stylized look plants one of the art's trees (made in
 * Blender, see docs/ART.md), a stand-in until the art has loaded; a sapling is a small one.
 */
import * as THREE from 'three';

import { ART_FAMILIES } from '../assets/manifest';
import type { Look } from '../style/look';
import type { Instances } from './instances';

const CROWN = new THREE.Color(0x86bc72);
const TALL = new THREE.Color(0x76b07c);
const CROWN_DARK = new THREE.Color(0x4f8a4e);
const TALL_DARK = new THREE.Color(0x467f55);
/** The art's trees, and how much darker a forest's trees are. */
const ART_TREES = ART_FAMILIES.get('trees') ?? [];
const FOREST = new THREE.Color(0.78, 0.84, 0.8);

const _tint = new THREE.Color();

/** One of the art's trees, picked by `h` (0..1); sets `_tint` a little lighter or darker, darker in a forest. */
function artTree(h: number, forest: boolean): string {
  const shade = 0.92 + 0.16 * ((h * 7.31) % 1);
  _tint.setScalar(shade);
  if (forest) _tint.multiply(FOREST);
  return ART_TREES[Math.floor(h * ART_TREES.length) % ART_TREES.length] ?? '';
}

/** A grown tree on its tile; `h` (0..1) picks its shape, `forest` makes it darker. */
export function plantTree(
  look: Look,
  ins: Instances,
  c: THREE.Vector3,
  yaw: number,
  scale: number,
  h: number,
  forest: boolean,
): void {
  if (look === 'stylized') {
    ins.put(artTree(h, forest), c, 0, 0, yaw, scale, _tint);
    return;
  }
  const tall = h < 0.4;
  ins.put('trunk', c, 0, 0, yaw, scale);
  ins.put(
    tall ? 'tall' : 'crown',
    c,
    0,
    0,
    yaw,
    scale,
    forest ? (tall ? TALL_DARK : CROWN_DARK) : tall ? TALL : CROWN,
  );
}

/** A sapling on its tile: a small tree. */
export function plantSapling(
  look: Look,
  ins: Instances,
  c: THREE.Vector3,
  yaw: number,
  h: number,
): void {
  if (look === 'stylized') {
    ins.put(artTree(h, false), c, 0, 0, yaw, 0.42 + 0.08 * h, _tint);
    return;
  }
  ins.put('trunk', c, 0, 0, yaw, 0.55);
  ins.put('crown', c, 0, 0, yaw, 0.42, CROWN);
}

/** One of the extra trees that make a forest dense, between its tiles; `tallOk`: it may be a tall one. */
export function plantBetween(
  look: Look,
  ins: Instances,
  c: THREE.Vector3,
  yaw: number,
  scale: number,
  h: number,
  tallOk: boolean,
): void {
  if (look === 'stylized') {
    ins.put(artTree(h, true), c, 0, 0, yaw, scale, _tint);
    return;
  }
  const tall = tallOk && h < 0.45;
  ins.put('trunk', c, 0, 0, yaw, scale);
  ins.put(tall ? 'tall' : 'crown', c, 0, 0, yaw, scale, tall ? TALL_DARK : CROWN_DARK);
}
